import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useLocalOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import { Styles, css } from '@flipster/styled-system/css';
import { center, stack, vstack } from '@flipster/styled-system/patterns';

import { authBunja } from '../../shared/auth/state';
import { ImgOrLottie } from '../assets';
import { ConvertModal } from './convert-modal';
import { luckyDrawBunja } from './state';

interface Props {
  index: number;
  handleRegister: () => void | Promise<unknown>;
  css?: Styles;
}

export const TokenCard = ({ index, handleRegister, css: cssProps }: Props) => {
  const router = useRouter();
  const { luckyDrawsLayout, luckyDrawsAtom, exchangeCostQueryAtom, isRegisteredAtom } = useBunja(luckyDrawBunja);
  const luckyDraws = useAtomValue(luckyDrawsAtom);
  const isRegistered = useAtomValue(isRegisteredAtom);
  const { isLoggedInAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { data: exchangeCosts } = useAtomValue(exchangeCostQueryAtom);

  const overlay = useLocalOverlay();

  const onGoing = luckyDraws.some((draw) => draw?.luckyDrawEventStatus === 'ONGOING');
  const currentLuckyDrawLayout = luckyDrawsLayout?.[index];
  const currentLuckyDraw = luckyDraws?.[index];

  if (!currentLuckyDrawLayout) return null;

  const fromLuckyDrawLayout = luckyDrawsLayout?.[index - 1];
  const fromLuckyDraw = luckyDraws?.[index - 1];
  const exchangeCost = exchangeCosts?.[currentLuckyDrawLayout.slug ?? ''];
  const isExchangeable = !!fromLuckyDrawLayout && !!exchangeCost;

  const handleClick = async () => {
    if (!isExchangeable) {
      requestAnimationFrame(() => {
        document.getElementById('missions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    if (!isLoggedIn) {
      snackbar.error({ message: <FormattedMessage id='Common.r250918.Please_log_in' /> });
      router.push('/signup');
      return;
    }
    if (!isRegistered) {
      await handleRegister();
      return;
    }
    if (fromLuckyDrawLayout?.tokenIcon && currentLuckyDrawLayout?.tokenIcon) {
      const fromTokenIcon = fromLuckyDrawLayout.tokenIcon;
      const currentTokenIcon = currentLuckyDrawLayout.tokenIcon;
      overlay.open(({ isOpen, onClose, unmount }) => (
        <ConvertModal
          fromTokenIcon={fromTokenIcon}
          fromTokens={fromLuckyDraw?.token ?? 0}
          toTokenIcon={currentTokenIcon}
          exchangeCost={exchangeCost}
          isOpen={isOpen}
          onClose={onClose}
          onExit={unmount}
        />
      ));
    }
  };

  return (
    <>
      <li
        className={css(
          vstack.raw({
            flex: 1,
            w: '100%',
            maxW: { tablet: 360 },
            py: { base: 40, tablet: 48 },
            px: 16,
            gap: { base: 8, tablet: 12 },
            rounded: 28,
          }),
          cssProps,
        )}
      >
        <div
          className={stack({
            pb: { base: 12, tablet: 28 },
            gap: 8,
          })}
        >
          <p
            className={css({
              textStyle: 'Title/L',
              color: 'inverse.white',
              textAlign: 'center',
            })}
          >
            {currentLuckyDrawLayout.tokenIcon?.title}
          </p>
          <p
            className={css({
              textStyle: 'Text/L/Strong',
              color: 'content.default.level-2',
              textAlign: 'center',
            })}
          >
            <FormattedMessage id='Campaign.r251124.You_have_colon' />{' '}
            <Formatted.Number
              value={currentLuckyDraw?.isRegistered ? currentLuckyDraw?.token : undefined}
              css={{ color: 'var(--key-color, {colors.inverse.white})' }}
            />
          </p>
        </div>
        <div
          className={center({
            aspectRatio: '1 / 1',
            flex: 1,
            w: '100%',
            maxW: { base: 120, tablet: 200 },
            maxH: { base: 120, tablet: 200 },
          })}
        >
          <ImgOrLottie url={currentLuckyDrawLayout?.tokenIcon?.url} alt='token icon' css={{ w: '100%', h: '100%' }} />
        </div>
        <Button
          hierarchy='primary'
          color='neutral'
          size='large'
          label={
            <FormattedMessage id={isExchangeable ? 'Common.r251124.Exchange' : 'Campaign.r251124.View_missions'} />
          }
          disabled={!onGoing}
          onClick={handleClick}
        />
      </li>
      {overlay.placeholder}
    </>
  );
};
